import React, { useState, useEffect, useRef } from 'react'
import { ArrowLeft, Play, Download, Trash2, Calendar, Clock, FileVideo, AlertCircle } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useClips } from '../../contexts/ClipsContext'
import { config } from '../../config/env'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog'
import { Button } from '../../components/ui/button'
import { LoadingScreen } from '../../components/ui/LoadingScreen'
import { storeAuthRedirectDestination } from '../../lib/authRedirect'

interface Clip {
  id: string
  filename: string
  title: string
  duration: number
  file_size: number
  storage_path: string
  stream_url?: string
  created_at: string
}

interface ClipsLibraryPageProps {
  onBack: () => void;
  /** Navigates to the clip‑creation flow */
  onStartClipping?: () => void;   // ← add (optional keeps old callers valid)
}


export const ClipsLibraryPage: React.FC<ClipsLibraryPageProps> = ({ onBack, onStartClipping,  }) => {
  const { user, signInWithGoogle } = useAuth()
  const { clips, loading, refreshClips, loadMoreClips, deleteClip: deleteClipFromContext, hasMore, totalCount, error } = useClips()
  const [selectedClip, setSelectedClip] = useState<Clip | null>(null)
  const [clipToDelete, setClipToDelete] = useState<Clip | null>(null)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const hasInitialized = useRef(false)

  useEffect(() => {
    if (user?.id && !hasInitialized.current) {
      hasInitialized.current = true
      // Load initial 10 clips for the clips page
      refreshClips(10)
    } else if (!user?.id) {
      hasInitialized.current = false
    }
  }, [user?.id, refreshClips])
  
  const handleDeleteClip = async (clipId: string) => {
    try {
      const success = await deleteClipFromContext(clipId)
      
      if (success) {
        if (selectedClip?.id === clipId) {
          setSelectedClip(null)
        }
        setClipToDelete(null)
        setShowDeleteDialog(false)
      } else {
        alert('Failed to delete clip')
      }
    } catch (error) {
      console.error('Error deleting clip:', error)
      alert('Error deleting clip')
    }
  }

  const handleLoadMore = async () => {
    if (loadingMore || !hasMore) return
    
    setLoadingMore(true)
    try {
      await loadMoreClips(10) // Load 10 more clips
    } finally {
      setLoadingMore(false)
    }
  }

  const formatFileSize = (bytes: number) => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    if (bytes === 0) return '0 Bytes'
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i]
  }

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading && clips === null) {
    return <LoadingScreen message="Loading your clips..." />;
  }

  return (
    <div className="min-h-screen relative">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">My Clips</h1>
              <p className="text-gray-600">
                {clips ? `${clips.length} of ${totalCount} clips` : 'Loading clips...'}
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div
            role="alert"
            className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-900"
          >
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-semibold">We could not update your clips.</p>
                <p className="mt-1 text-sm">{error.message}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    onClick={() => void refreshClips(10)}
                  >
                    Retry
                  </Button>
                  {error.kind === 'authentication' && (
                    <Button
                      onClick={() => {
                        storeAuthRedirectDestination('/clips')
                        void signInWithGoogle()
                      }}
                    >
                      Sign in again
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {error && clips === null ? null : !clips || clips.length === 0 ? (
          <div className="text-center py-16">
            <div className="bg-gray-100 p-6 rounded-full inline-block mb-6">
              <FileVideo className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No clips yet</h3>
            <p className="text-gray-600 mb-6">Create your first viral clip to see it here</p>
            <Button
              onClick={onBack}
              className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-6 rounded-xl transition-all"
            >
              Create Your First Clip
            </Button>
          </div>
        ) : (
          <>
            {/* Modern TikTok-Style Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {clips.map((clip) => (
                <div
                  key={clip.id}
                  className="group relative bg-black rounded-2xl overflow-hidden hover:scale-105 transition-all duration-300 cursor-pointer shadow-lg hover:shadow-2xl"
                  onClick={() => setSelectedClip(clip)}
                >
                  {/* TikTok Aspect Ratio Video Preview */}
                  <div className="aspect-[9/16] relative overflow-hidden">
                    {clip.stream_url ? (
                      <video
                        src={clip.stream_url}
                        className="w-full h-full object-cover"
                        poster=""
                        muted
                        onMouseEnter={(e) => {
                          const video = e.currentTarget;
                          video.currentTime = 2; // Start preview at 2 seconds
                          video.play().catch(() => {});
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.pause();
                          e.currentTarget.currentTime = 0;
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                        <FileVideo className="w-12 h-12 text-gray-400" />
                      </div>
                    )}
                    
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    
                    {/* Duration Badge */}
                    <div className="absolute top-3 right-3 bg-black/80 backdrop-blur-sm text-white px-2 py-1 rounded-full text-xs font-medium">
                      {formatDuration(clip.duration)}
                    </div>
                    
                    {/* Action Buttons - Show on Hover */}
                    <div className="absolute bottom-3 left-3 right-3 flex justify-between items-end opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                      <div className="flex-1">
                        <h3 className="text-white font-bold text-sm mb-1 truncate drop-shadow-lg">
                          {clip.title}
                        </h3>
                        <p className="text-white/80 text-xs truncate drop-shadow">
                          {formatDate(clip.created_at)}
                        </p>
                      </div>
                      
                      <div className="flex gap-2 ml-2">
                        <Button
                          onClick={(e) => {
                            e.stopPropagation()
                            if (clip.stream_url) {
                              window.open(clip.stream_url, '_blank')
                            }
                          }}
                          size="sm"
                          className="bg-blue-500/90 hover:bg-blue-500 text-white p-2 rounded-full transition-all duration-200 backdrop-blur-sm hover:scale-110"
                          title="Download"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation()
                            setClipToDelete(clip)
                            setShowDeleteDialog(true)
                          }}
                          size="sm"
                          className="bg-red-500/90 hover:bg-red-500 text-white p-2 rounded-full transition-all duration-200 backdrop-blur-sm hover:scale-110"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Play Icon Overlay */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="bg-white/20 backdrop-blur-sm rounded-full p-3">
                        <Play className="w-8 h-8 text-white fill-current" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Load More Button */}
            {hasMore && (
              <div className="text-center mt-8">
                <Button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-8 rounded-xl transition-all"
                >
                  {loadingMore ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Loading more clips...
                    </>
                  ) : (
                    `View More (${totalCount - clips.length} remaining)`
                  )}
                </Button>
              </div>
            )}
            
            {/* Modern Modal for Selected Clip */}
            {selectedClip && (
              <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedClip(null)}>
                <div className="bg-white rounded-3xl max-w-sm w-full max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
                  {/* Video Player */}
                  {selectedClip.stream_url && (
                    <div className="aspect-[4/3] sm:aspect-[9/16] bg-black relative overflow-hidden rounded-t-3xl flex-shrink-0">
                      <video
                        src={selectedClip.stream_url}
                        controls
                        className="w-full h-full object-cover"
                        autoPlay
                        controlsList="nodownload"
                        style={{
                          outline: 'none',
                        }}
                      />
                      <Button
                        onClick={() => setSelectedClip(null)}
                        size="sm"
                        variant="ghost"
                        className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 text-white rounded-full w-8 h-8 flex items-center justify-center transition-all z-10 p-0"
                      >
                        ✕
                      </Button>
                    </div>
                  )}
                  
                  {/* Clip Details */}
                  <div className="flex-1 flex flex-col min-h-0">
                    <div className="flex-1 p-6 overflow-y-auto">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">{selectedClip.title}</h3>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm mb-6">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Clock className="w-4 h-4" />
                        {formatDuration(selectedClip.duration)}
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <FileVideo className="w-4 h-4" />
                        {formatFileSize(selectedClip.file_size)}
                      </div>
                      <div className="flex items-center gap-2 text-gray-600 col-span-2">
                        <Calendar className="w-4 h-4" />
                        {formatDate(selectedClip.created_at)}
                      </div>
                    </div>

                    </div>

                    {/* Fixed buttons at bottom */}
                    <div className="p-6 pt-0 border-t border-gray-100">
                      <div className="flex gap-3">
                        <Button
                          onClick={() => {
                            if (selectedClip.stream_url) {
                              window.open(selectedClip.stream_url, '_blank')
                            }
                          }}
                          className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold py-3 px-4 rounded-2xl transition-all flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
                        >
                          <Download className="w-5 h-5" />
                          Download
                        </Button>
                        <Button
                          onClick={() => {
                            setClipToDelete(selectedClip)
                            setShowDeleteDialog(true)
                          }}
                          className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold py-3 px-4 rounded-2xl transition-all shadow-lg hover:shadow-xl"
                        >
                          <Trash2 className="w-5 h-5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
              <DialogContent className="sm:max-w-md bg-white border border-gray-200 shadow-xl">
                <DialogHeader className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 bg-blue-50 rounded-full flex items-center justify-center">
                    <AlertCircle className="w-8 h-8 text-blue-600" />
                  </div>
                  <DialogTitle className="text-gray-900 text-xl font-bold">Delete Clip</DialogTitle>
                  <DialogDescription className="text-gray-600">
                    Are you sure you want to delete "{clipToDelete?.title}"? This action cannot be undone.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter className="flex gap-3 sm:gap-3">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowDeleteDialog(false)
                      setClipToDelete(null)
                    }}
                    className="flex-1 bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={() => clipToDelete && handleDeleteClip(clipToDelete.id)}
                    className="flex-1 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white"
                  >
                    Delete
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}
      </div>
    </div>
  )
}
